import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Alert,
  Box,
  Skeleton,
  Stack,
  Toolbar,
  IconButton,
  Divider,
  CircularProgress,
} from '@mui/material';
import { ArrowBack, Check, Close } from '@mui/icons-material';
import { Link } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useUser } from '@/providers';

import DataReview from './DataReview';
import EnvironmentReview from './EnvironmentReview';
import ModelReview from './ModelReview';
import ResourcesReview from './ResourcesReview';
import WorkerReview from './WorkerReview';
import BytesToMinimumGB from '@/util/BytesToMinimumGB';
import JOB_TYPES from '@/components/jobs/types';
import EndpointReview from './EndpointReview';
import api from '@/apis/api';
import NoGpusModal from '@/components/jobs/NoGpusModal';
import { clearErrorMessage } from '@/actions/errorMessage';

export default function JobFormReview({
  jobRequest,
  submitting,
  credits_per_hour_min,
  credits_per_hour_max,
  max_price_too_low,
  onSubmit,
  onCancel,
}) {
  const [available, setAvailable] = useState(null);
  const [loading, setLoading] = useState(true);
  const user = useUser();
  const error_message = useSelector((state) => state.errorMessage);
  const projects = useSelector((state) => state.projects);
  const selected_project = projects?.find(
    (project) => project.id === user?.selected_project,
  );
  const owned_resources_only = Boolean(selected_project?.owned_resources_only);
  const dispatch = useDispatch();

  useEffect(() => {
    window.scrollTo(0, 0);
    const {
      gpu_types,
      gpu_count,
      cpu_count,
      max_price,
      disk_size,
      preemptible,
    } = jobRequest.resources;
    const fetchAvailable = async () => {
      try {
        const res = await api.get(
          `/project/${user.selected_project}/gputypes/available`,
          {
            params: {
              gpu_types: JSON.stringify(gpu_types),
              gpu_count,
              cpu_count,
              max_price,
              disk_size,
              preemptible,
            },
          },
        );
        setAvailable(res.data);
        setLoading(false);
      } catch (error) {
        console.log(error);
        console.log(error.response);
        setLoading(false);
      }
    };
    fetchAvailable();
  }, [jobRequest.resources, user.selected_project]);

  const handleSubmit = async () => {
    onSubmit();
  };

  const handleCancel = (e) => {
    e.preventDefault();
    onCancel();
  };

  const renderCreditsMessage = () => {
    const not_enough_credits = Boolean(
      !owned_resources_only &&
        !user.cloudbender_enabled &&
        user.credits <= credits_per_hour_min &&
        !user.billing_account_id &&
        user.id === user.selected_project,
    );
    if (max_price_too_low) {
      return (
        <Alert severity='error' sx={{ mb: 2 }}>
          <Typography variant='h6'>Maximum Price Too Low</Typography>
          <Typography>
            The maximum price per GPU requested is lower than the minimum
            available price per GPU.
          </Typography>
        </Alert>
      );
    } else if (owned_resources_only) {
      return null;
    } else if (not_enough_credits && !submitting) {
      return (
        <Alert severity='error' sx={{ mb: 2 }}>
          <Typography variant='h6'>Not Enough Credits</Typography>
          <Typography>
            Please{' '}
            <Link to='/account/billing' target='_blank'>
              purchase credits
            </Link>{' '}
            in order to create a job.
          </Typography>
        </Alert>
      );
    } else {
      return (
        <Alert severity='info' sx={{ mb: 2 }}>
          <Typography variant='h6'>
            You will be billed{' '}
            {credits_per_hour_min === credits_per_hour_max
              ? credits_per_hour_max
              : `between ${credits_per_hour_min} and ${credits_per_hour_max}`}{' '}
            credits each hour this job runs.
          </Typography>
        </Alert>
      );
    }
  };

  if (!jobRequest) {
    return <div>No data</div>;
  }

  const not_enough_credits = Boolean(
    !owned_resources_only &&
      !user.cloudbender_enabled &&
      user.credits <= credits_per_hour_min &&
      !user.billing_account_id &&
      user.id === user.selected_project,
  );

  const { name, type, data, environment, model, endpoint, workers, resources } =
    jobRequest;

  return (
    <Container>
      <Paper sx={{ display: 'flex', width: '100%' }} elevation={2}>
        <Grid container spacing={2} sx={{ flexGrow: 1 }}>
          <Grid size={12}>
            <Paper sx={{ display: 'flex', width: '100%' }} elevation={2}>
              <Toolbar>
                <Stack spacing={2} direction='row' alignItems='center'>
                  <IconButton onClick={handleCancel} disabled={submitting}>
                    <ArrowBack />
                  </IconButton>
                  <Typography variant='h5'>Review: {name}</Typography>
                </Stack>
              </Toolbar>
            </Paper>
          </Grid>

          <Grid size={12}>
            <Box padding='15px'>
              {loading ? (
                <Box>
                  <Skeleton variant='text' height={40} />
                  <Skeleton variant='text' height={40} />
                  <Skeleton variant='rectangular' height={200} />
                  <Skeleton variant='rectangular' height={200} />
                </Box>
              ) : (
                <>
                  <ResourcesReview
                    data={{
                      ...resources,
                      available,
                      type,
                      worker_count: workers.length,
                      datasets_size: data.datasets
                        ? data.datasets.length
                          ? data.datasets
                              .map((dataset) =>
                                BytesToMinimumGB(
                                  dataset.size || dataset.used_size,
                                ),
                              )
                              .reduce((a, b) => a + b)
                          : 0
                        : 0,
                    }}
                  />
                  {![JOB_TYPES.LLM.value, JOB_TYPES.GUARDRAIL.value].includes(
                    type,
                  ) &&
                    ((data.datasets && data.datasets.length) ||
                      data.output_type ||
                      data.input_type ||
                      (data.volumes && data.volumes.length) ||
                      (data.data_connectors && data.data_connectors.length) ||
                      (data.private_services &&
                        data.private_services.length)) && <DataReview data={data} />}
                  {type !== JOB_TYPES.GUARDRAIL.value &&
                    (type === JOB_TYPES.LLM.value
                      ? Boolean(model?.checkpoints?.length)
                      : Boolean(
                          model?.source_uri || model?.checkpoints?.length,
                        )) && <ModelReview data={model} />}
                  {[
                    JOB_TYPES.TRAINING.value,
                    JOB_TYPES.INFERENCE.value,
                  ].includes(type) ? (
                    <WorkerReview type={type} data={workers} />
                  ) : [
                      JOB_TYPES.ENDPOINT.value,
                      JOB_TYPES.LLM.value,
                      JOB_TYPES.GUARDRAIL.value,
                    ].includes(type) ? (
                    <EndpointReview type={type} data={endpoint} />
                  ) : null}
                  {![JOB_TYPES.LLM.value, JOB_TYPES.GUARDRAIL.value].includes(
                    type,
                  ) ? (
                    <EnvironmentReview data={environment} />
                  ) : null}
                  {renderCreditsMessage()}
                </>
              )}
            </Box>
          </Grid>

          <Grid size={12}>
            <Divider />
            <Toolbar>
              <Stack spacing={2} direction='row'>
                <Button
                  variant='outlined'
                  startIcon={<Close />}
                  onClick={handleCancel}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  variant='contained'
                  color='brand'
                  disabled={
                    not_enough_credits ||
                    available === null ||
                    max_price_too_low ||
                    submitting
                  }
                  endIcon={<Check />}
                  startIcon={submitting ? <CircularProgress size={20} /> : undefined}
                  onClick={handleSubmit}
                >
                  Create
                </Button>
              </Stack>
            </Toolbar>
          </Grid>
        </Grid>
      </Paper>
      <NoGpusModal
        errorMessage={error_message}
        clearError={() => dispatch(clearErrorMessage())}
      />
    </Container>
  );
}
